// ============================================================================
// TodayHero — the "Today" centrepiece of the fancy client dashboard (Wave 4).
// ----------------------------------------------------------------------------
// A single hero section fed by GET /api/client/me/home: a BIG primary Calories
// ring flanked by smaller protein / carbs / fat / water rings, each showing
// today's logged intake vs the resolved daily target. The source label
// ("Recommended" vs "Set by your coach") tells the client where the targets
// came from — rendered via the EXISTING nutrition i18n keys (target.auto /
// target.coach / target.source), never a server-shipped display string.
//
// Layout is mobile-first: on a 390px phone the kcal ring stands large and
// centred with the four macro/water rings in a 2-col grid beneath it; from sm
// up the macro rings flow into a single row beside / under the hero for a
// gorgeous, balanced composition on tablet. Framer Motion gives the section a
// gentle staggered entrance; every ring guards animation via useReducedMotion
// (in ProgressRing) so it degrades to the final state for reduced-motion users.
//
// Reuses: ProgressRing (this wave) + the nutrition namespace + the home hook.
// The non-medical disclaimer is rendered from the i18n key (nutrition:disclaimer)
// so it always matches the live UI language — NOT the server's home.disclaimer.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Banner, Card, CardHeader } from "../../components";
import type { BadgeTone } from "../../components";
import { ProgressRing } from "./ProgressRing";
import { fmtNum } from "./portalLib";
import type { ClientHomeNutrition, ClientHomeWater } from "../../api";

/** Build the localized "{{logged}} of {{target}} {{unit}}" caption (or logged). */
function useCaption() {
  const { t } = useTranslation("nutrition");
  return (logged: number, target: number | null, unit: string): string =>
    target != null && target > 0
      ? t("target.loggedOfTarget", {
          logged: fmtNum(logged, 0),
          target: fmtNum(target, 0),
          unit,
        })
      : `${fmtNum(logged, 0)} ${unit}`;
}

export function TodayHero({
  nutrition,
  water,
}: {
  nutrition: ClientHomeNutrition;
  water: ClientHomeWater;
}) {
  const { t } = useTranslation("nutrition");
  const reduce = useReducedMotion();
  const caption = useCaption();

  const { intake, targets } = nutrition;
  const kcal = intake.kcal ?? 0;
  const protein = intake.protein_g ?? 0;
  const carbs = intake.carbs_g ?? 0;
  const fat = intake.fat_g ?? 0;
  const waterMl = water.ml ?? 0;

  const sourceLabel =
    targets.source === "coach" ? t("target.coach") : t("target.auto");
  // Coach-set targets get a violet "ember" badge; auto guidance a neutral one.
  const sourceTone: BadgeTone = targets.source === "coach" ? "ember" : "neutral";
  const macrosMissing = targets.kcal == null && targets.protein_g == null;

  // Stagger the rings in (kcal first, then macros, then water). Disabled cleanly
  // under reduced motion (initial=false → render final state, no transition).
  const ring = (i: number) => ({
    initial: reduce ? false : { opacity: 0, scale: 0.9, y: 8 },
    animate: { opacity: 1, scale: 1, y: 0 },
    transition: {
      duration: 0.4,
      delay: reduce ? 0 : 0.05 + i * 0.06,
      ease: [0.22, 0.61, 0.36, 1] as const,
    },
  });

  return (
    <Card className="space-y-5 overflow-hidden">
      <CardHeader
        title={t("target.title")}
        subtitle={`${t("target.source")}: ${sourceLabel}`}
        action={
          <Badge tone={sourceTone} dot>
            {sourceLabel}
          </Badge>
        }
      />

      {/* Hero: the big Calories ring. Centred on phone; sits left on wider. */}
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:justify-center sm:gap-8">
        <motion.div {...ring(0)} className="shrink-0">
          <ProgressRing
            value={kcal}
            target={targets.kcal}
            label={t("macro.kcal")}
            unit={t("macro.kcalUnit")}
            colour="coral"
            size={172}
            decimals={0}
            delay={reduce ? 0 : 0.1}
            ariaValueText={caption(kcal, targets.kcal, t("macro.kcalUnit"))}
          />
        </motion.div>

        {/* Macro + water rings: 2-col on phone, a tidy row from sm up. */}
        <div className="grid w-full grid-cols-2 gap-x-2 gap-y-5 sm:w-auto sm:grid-cols-4 sm:gap-x-5">
          <motion.div {...ring(1)} className="flex justify-center">
            <ProgressRing
              value={protein}
              target={targets.protein_g}
              label={t("macro.protein")}
              unit={t("macro.gUnit")}
              colour="violet"
              size={96}
              decimals={0}
              delay={reduce ? 0 : 0.18}
              ariaValueText={caption(protein, targets.protein_g, t("macro.gUnit"))}
            />
          </motion.div>
          <motion.div {...ring(2)} className="flex justify-center">
            <ProgressRing
              value={carbs}
              target={targets.carbs_g}
              label={t("macro.carbs")}
              unit={t("macro.gUnit")}
              colour="amber"
              size={96}
              decimals={0}
              delay={reduce ? 0 : 0.24}
              ariaValueText={caption(carbs, targets.carbs_g, t("macro.gUnit"))}
            />
          </motion.div>
          <motion.div {...ring(3)} className="flex justify-center">
            <ProgressRing
              value={fat}
              target={targets.fat_g}
              label={t("macro.fat")}
              unit={t("macro.gUnit")}
              colour="lime"
              size={96}
              decimals={0}
              delay={reduce ? 0 : 0.3}
              ariaValueText={caption(fat, targets.fat_g, t("macro.gUnit"))}
            />
          </motion.div>
          <motion.div {...ring(4)} className="flex justify-center">
            <ProgressRing
              value={waterMl}
              target={water.target_ml}
              label={t("water.title")}
              unit={t("water.mlUnit")}
              colour="ocean"
              size={96}
              decimals={0}
              delay={reduce ? 0 : 0.36}
              ariaValueText={caption(waterMl, water.target_ml, t("water.mlUnit"))}
            />
          </motion.div>
        </div>
      </div>

      {/* When the profile lacks the BMR/TDEE inputs, the macro targets are null:
          the rings still render the logged figures, and this hint nudges the
          client to fill in their stats to unlock targets. */}
      {macrosMissing && <Banner tone="info" title={t("target.needMore")} />}

      {/* Non-medical disclaimer — rendered from the i18n key, NOT the server
          string, so it always tracks the live UI language (Wave 2 leak guard). */}
      <p className="rounded-md bg-surface-sunken px-3 py-2.5 text-xs leading-relaxed text-body-muted">
        {t("disclaimer")}
      </p>
    </Card>
  );
}
